import { useCallback, useEffect, useRef, useState } from 'react';

import { Grade } from '../../../../components/_molecules/SettingsModalDesigner/gradeAndSubject';
import { UserAccessBadge } from '../../../../hooks/useAccessBadge';
import { isInternationalCountry } from '../../../../utils/internationalCountries';
import { SubjectType } from '../../../_organisms/DesignerTabPane/AgentTab/AgentHooks';
import { HOME_GRADE_CHANGED_EVENT } from './createFlowUtils';
import { ALL_HOME_SUBJECTS, resolveSubjectValue } from './homeSubjectOptions';

export const LOCAL_STORAGE_KEYS = {
  grade: '@curipod_v1_home_grade',
  subject: '@curipod_v1_home_subject',
  // CURI-301 one-time migration flag: bumps international users' home-page
  // subject from the previous 'ela' default to 'english' so they immediately
  // see English content after the PR ships. Per-browser, so the migration
  // runs once per device.
  internationalEnglishMigration: '@curipod_v1_home_intl_english_migration',
} as const;

export const GRADE_OPTIONS = [
  'K',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
] as const;
export const SUBJECT_OPTIONS = ALL_HOME_SUBJECTS;

const GRADE_MAP: Record<string, string> = {
  Kindergarten: 'K',
  '1st': '1',
  '2nd': '2',
  '3rd': '3',
  '4th': '4',
  '5th': '5',
  '6th': '6',
  '7th': '7',
  '8th': '8',
  '9th': '9',
  '10th': '10',
  '11th': '11',
  '12th': '12',
};

function getStored<T>(key: string): T | undefined {
  try {
    const data = localStorage.getItem(key);
    return data ? (JSON.parse(data) as { value: T }).value : undefined;
  } catch {
    return undefined;
  }
}

function isValidOption<T extends readonly string[]>(
  value: string | undefined,
  options: T,
): value is T[number] {
  return value !== undefined && options.includes(value as T[number]);
}

function getInitialValue<T extends string>(
  storageKey: string,
  options: readonly string[],
  profileValue: string | undefined,
  // A function rather than a lookup table because subject and grade disagree
  // about unknown values: an unrecognised subject is the teacher's "Other"
  // answer, while an unrecognised grade has no equivalent and must fall
  // through to the default.
  mapProfileValue: (value: string) => string | undefined,
  defaultValue: T,
): T {
  const stored = getStored<string>(storageKey);
  if (isValidOption(stored, options)) return stored as T;

  if (profileValue) {
    const mapped = mapProfileValue(profileValue);
    if (isValidOption(mapped, options)) return mapped as T;
  }

  return defaultValue;
}

export function useHomeSelectorsState(accessBadge: UserAccessBadge | undefined) {
  const profile = accessBadge?.user.profileInfo;
  const country = accessBadge?.organization?.country ?? undefined;

  const initialValues = useRef<{ grade: Grade; subject: SubjectType } | null>(null);

  const [grade, setGradeState] = useState<Grade>(() => {
    const value = getInitialValue(
      LOCAL_STORAGE_KEYS.grade,
      GRADE_OPTIONS,
      profile?.grades?.[0],
      v => GRADE_MAP[v],
      GRADE_OPTIONS[4], // Default to 4th grade
    );
    if (!initialValues.current) {
      initialValues.current = { grade: value, subject: SUBJECT_OPTIONS[0] };
    }
    initialValues.current.grade = value;
    return value;
  });
  const [subject, setSubjectState] = useState<SubjectType>(() => {
    // International users with no stored or profile subject default to
    // 'english' so they land on the English content immediately (CURI-301).
    // When a profile subject is present we honour it via resolveSubjectValue —
    // so 'English' → 'english', 'Literacy' → 'literacy', 'English Language
    // Arts' → 'ela', other known subjects (Math, Science...) pass through, and
    // a free-text answer ('CTE') becomes 'other'.
    const defaultSubject: SubjectType = isInternationalCountry(country)
      ? 'english'
      : SUBJECT_OPTIONS[0];
    const value = getInitialValue(
      LOCAL_STORAGE_KEYS.subject,
      SUBJECT_OPTIONS,
      profile?.subjects?.[0],
      resolveSubjectValue,
      defaultSubject,
    );
    if (initialValues.current) {
      initialValues.current.subject = value;
    }
    return value;
  });

  const syncedFields = useRef({ grade: false, subject: false });

  // Hoisted above the useEffect that calls it so the read-time-vs-call-time
  // dance through the temporal dead zone isn't load-bearing.
  const save = (key: string, value: string) =>
    localStorage.setItem(key, JSON.stringify({ value }));

  useEffect(() => {
    if (!accessBadge) return;

    // CURI-301 one-time migration: bump international users to 'english' the
    // first time they load the home page after the PR ships. Only override
    // when the user was sitting on the previous default ('ela') — either
    // explicitly stored, or implied by an absent-stored + no-profile-subject
    // combo, or a profile subject that mapped to 'ela'. If the user already
    // picked something specific (Math, English, Literacy, …) we respect it
    // and just flip the migration flag so we never check again.
    //
    // We also short-circuit when the org has a recorded `isInternational`
    // answer — that means the teacher has been through onboarding (or the
    // home modal) post-CURI-301 and their choice is the source of truth.
    // No need to second-guess it via the migration.
    const orgHasIntlSchoolAnswer = accessBadge?.organization?.isInternational != null;
    const migrationDone =
      orgHasIntlSchoolAnswer ||
      !!getStored(LOCAL_STORAGE_KEYS.internationalEnglishMigration);
    if (isInternationalCountry(country) && !migrationDone) {
      const stored = getStored<string>(LOCAL_STORAGE_KEYS.subject);
      const profileSubject = profile?.subjects?.[0];
      const profileMapped = profileSubject
        ? resolveSubjectValue(profileSubject)
        : undefined;
      const wasOnPreviousDefault = stored
        ? stored === 'ela'
        : !profileMapped || profileMapped === 'ela';
      if (wasOnPreviousDefault) {
        setSubjectState('english');
        save(LOCAL_STORAGE_KEYS.subject, 'english');
        // Also bump the snapshot used by `reset()`. Without this a
        // browser-back / query-param reset on the home page would
        // revert the migrated user from 'english' back to the
        // pre-migration 'ela' default and silently undo the migration.
        if (initialValues.current) {
          initialValues.current.subject = 'english';
        }
        // Block the profile sync below from re-deriving from
        // profile.subjects (which might still say 'English Language Arts').
        syncedFields.current.subject = true;
      }
      save(LOCAL_STORAGE_KEYS.internationalEnglishMigration, 'done');
    }

    const syncFromProfile = (
      key: keyof typeof syncedFields.current,
      storageKey: string,
      options: readonly string[],
      profileValue: string | undefined,
      mapProfileValue: (value: string) => string | undefined,
    ): string | null => {
      if (getStored(storageKey) || syncedFields.current[key] || !profileValue)
        return null;
      const mapped = mapProfileValue(profileValue);
      if (isValidOption(mapped, options)) {
        syncedFields.current[key] = true;
        return mapped;
      }
      return null;
    };

    const newGrade = syncFromProfile(
      'grade',
      LOCAL_STORAGE_KEYS.grade,
      GRADE_OPTIONS,
      profile?.grades?.[0],
      v => GRADE_MAP[v],
    );
    if (newGrade) setGradeState(newGrade as Grade);

    const newSubject = syncFromProfile(
      'subject',
      LOCAL_STORAGE_KEYS.subject,
      SUBJECT_OPTIONS,
      profile?.subjects?.[0],
      resolveSubjectValue,
    );
    if (newSubject) setSubjectState(newSubject as SubjectType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessBadge]);

  useEffect(() => {
    const handler = (e: Event) => {
      const next = (e as CustomEvent<string>).detail;
      if (isValidOption(next, GRADE_OPTIONS)) {
        setGradeState(prev => (prev === next ? prev : (next as Grade)));
      }
    };
    window.addEventListener(HOME_GRADE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(HOME_GRADE_CHANGED_EVENT, handler);
  }, []);

  // Persist the in-memory selector values to localStorage when nothing is
  // stored yet. Without this, readers that consult localStorage directly
  // (e.g. getHomeGrade() used by category-page card badges) see `undefined`
  // until the user changes the dropdown — even though the dropdown already
  // shows a default. That's what made Vocabulary Hustle's badge render the
  // grade band ("K-12th") instead of the user's exact grade.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!getStored(LOCAL_STORAGE_KEYS.grade)) {
      save(LOCAL_STORAGE_KEYS.grade, grade);
      window.dispatchEvent(new CustomEvent(HOME_GRADE_CHANGED_EVENT, { detail: grade }));
    }
  }, [grade]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!getStored(LOCAL_STORAGE_KEYS.subject)) {
      save(LOCAL_STORAGE_KEYS.subject, subject);
    }
  }, [subject]);

  const setGrade = useCallback((v: Grade) => {
    setGradeState(v);
    save(LOCAL_STORAGE_KEYS.grade, v);
    // Rebase what `reset` restores. `reset` is for the home page's browsing
    // state — the open card, the location — and it fires on browser Back and on
    // the sidebar's Home link. Restoring the grade the page happened to mount
    // with silently threw away the grade the teacher had just chosen, whether
    // they chose it in this dropdown or in the curriculum page's, which now
    // writes through here too.
    if (initialValues.current) initialValues.current.grade = v;
  }, []);
  const setSubject = useCallback((v: SubjectType) => {
    // Onboarding reaches this through HomePage's `onComplete` as
    // `setSubject(s as any)`, and the value it passes is derived from a
    // free-text "Other" answer via `subject.toLowerCase()`. A teacher who
    // typed "CTE" would otherwise put `cte` into state and localStorage, and
    // the dropdown has no option for it, so it renders the raw string.
    // (It also can't be recovered from until a reload discards it.)
    if (!isValidOption(v, SUBJECT_OPTIONS)) return;
    setSubjectState(v);
    save(LOCAL_STORAGE_KEYS.subject, v);
  }, []);

  const reset = useCallback(() => {
    if (initialValues.current) {
      setGradeState(initialValues.current.grade);
      setSubjectState(initialValues.current.subject);
      save(LOCAL_STORAGE_KEYS.grade, initialValues.current.grade);
      save(LOCAL_STORAGE_KEYS.subject, initialValues.current.subject);
    }
  }, []);

  return { subject, grade, setSubject, setGrade, reset };
}
